
import { setCustomParticipantVisible } from '../base/conference';
import { isMobileBrowser, isMobileResponsive } from '../base/environment/utils';
import { MHParticipantConnectionStatus } from '../base/lib-meet-hour';
import { MEDIA_TYPE, VIDEO_TYPE } from '../base/media';
import { FakeParticipant } from '../base/mtApi';
import {
    getLocalParticipant,
    getParticipantById,
    getParticipantCountWithFake,
    getPinnedParticipant,
    getSharedVideoParticipant,
    isSharedThumbnailEnabled,
    pinParticipant
} from '../base/participants';
import { toState } from '../base/redux';
import {
    getLocalVideoTrack,
    getScreenShareTrack,
    getTrackByMediaTypeAndParticipant,
    isLocalTrackMuted,
    isRemoteTrackMuted
} from '../base/tracks/functions';
import { exitTileViewIfNeeded } from '../conference/filmStripViewManager';
import { getFocusModeVisibleParticipants } from '../focus-mode/functions';
import { isVideoPlaying } from '../shared-video/functions';
import { LAYOUTS } from '../video-layout';
import { iAmVisitor } from '../visitors/functions';
import {
    getWebinarAudienceRemoteParticipants,
    isAudienceLayoutActive,
    isPresenterHiddenForLocal
} from '../webinar/functions';
import {
    ZOOM_SYNC_EPSILON,
    flipZoomStateX,
    toTransform
} from '../zoom-sync';

import {
    ASPECT_RATIO_BREAKPOINT,
    DISPLAY_AVATAR,
    DISPLAY_AVATAR_WITH_NAME,
    DISPLAY_BLACKNESS_WITH_NAME,
    DISPLAY_VIDEO,
    DISPLAY_VIDEO_WITH_NAME,
    SQUARE_TILE_ASPECT_RATIO,
    TILE_ASPECT_RATIO
} from './constants';

declare var $: Object;
declare var APP: Object;
declare var interfaceConfig: Object;

// Minimum space to keep between the sides of the tiles and the sides
// of the window.
const TILE_VIEW_SIDE_MARGINS = 20;

/**
 * Returns true if the filmstrip on mobile is visible, false otherwise.
 *
 * NOTE: Filmstrip on web behaves differently to mobile, much simpler, but so
 * function lies here only for the sake of consistency and to avoid flow errors
 * on import.
 *
 * @param {Object | Function} stateful - The Object or Function that can be
 * resolved to a Redux state object with the toState function.
 * @returns {boolean}
 */
export function isFilmstripVisible(stateful: Object | Function) {
    return toState(stateful)['features/filmstrip'].visible;
}

/**
 * Gets the count of visible participants in the current page of the filmstrip.
 *
 * @param {Object | Function} stateful - The Object or Function that can be
 * resolved to a Redux state object with the toState function.
 * @returns {number} - The count of visible participants in the current page.
 */
export function getVisibleParticipantsCount(stateful: Object | Function) {
    return toState(stateful)['features/filmstrip'].visibleParticipantsCount;
}

/**
 * Determines whether the remote video thumbnails should be displayed/visible in
 * the filmstrip.
 *
 * @param {Object} state - The full redux state.
 * @returns {boolean} - If remote video thumbnails should be displayed/visible
 * in the filmstrip, then {@code true}; otherwise, {@code false}.
 */
export function shouldRemoteVideosBeVisible(state: Object) {
    if (state['features/invite'].calleeInfoVisible) {
        return false;
    }

    // Include fake participants to derive how many thumbnails are dispalyed,
    // as it is assumed all participants, including fake, will be displayed
    // in the filmstrip.
    const participantCount = getParticipantCountWithFake(state);
    let pinnedParticipant;

    return Boolean(
    participantCount > 2

      // Always show the filmstrip when there is another participant to
      // show and the  local video is pinned, or the toolbar is displayed.
      || (participantCount > 1
        && (state['features/toolbox'].visible
          || ((pinnedParticipant = getPinnedParticipant(state))
            && pinnedParticipant.local)))
      || state['features/base/config'].disable1On1Mode
    );
}

/**
 * Checks whether there is a playable video stream available for the user associated with the passed ID.
 *
 * @param {Object | Function} stateful - The Object or Function that can be
 * resolved to a Redux state object with the toState function.
 * @param {string} id - The id of the participant.
 * @returns {boolean} <tt>true</tt> if there is a playable video stream available
 * or <tt>false</tt> otherwise.
 */
export function isVideoPlayable(stateful: Object | Function, id: String) {
    const state = toState(stateful);
    const tracks = state['features/base/tracks'];
    const participant = id
        ? getParticipantById(state, id)
        : getLocalParticipant(state);
    const isLocal = participant?.local ?? true;
    const { connectionStatus } = participant || {};
    const screenShareTrack = id
        ? getTrackByMediaTypeAndParticipant(tracks, MEDIA_TYPE.SCREENSHARE, id)
            || getScreenShareTrack(tracks, id)
        : undefined;
    const videoTrack = isLocal
        ? screenShareTrack || getLocalVideoTrack(tracks)
        : screenShareTrack || getTrackByMediaTypeAndParticipant(tracks, MEDIA_TYPE.VIDEO, id);
    const isAudioOnly = Boolean(state['features/base/audio-only'].enabled);
    let isPlayable = false;

    if (isLocal) {
        const isVideoMuted = isLocalTrackMuted(tracks, MEDIA_TYPE.VIDEO);

        isPlayable = Boolean(videoTrack) && !isVideoMuted && !isAudioOnly;
    } else if (!participant?.isFakeParticipant) {
    // remote participants excluding shared video
        const isVideoMuted = isRemoteTrackMuted(tracks, MEDIA_TYPE.VIDEO, id);

        isPlayable
      = Boolean(videoTrack)
      && !isVideoMuted
      && !isAudioOnly
      && connectionStatus === MHParticipantConnectionStatus.ACTIVE;
    }

    return isPlayable;
}

/**
 * Calculates the size for thumbnails when in horizontal view layout.
 *
 * @param {number} clientHeight - The height of the app window.
 * @returns {{local: {height, width}, remote: {height, width}}}
 */
export function calculateThumbnailSizeForHorizontalView(
        clientHeight: number = 0
) {
    const topBottomMargin = 15;
    const availableHeight = Math.min(
    clientHeight,
    (interfaceConfig.FILM_STRIP_MAX_HEIGHT || 120) + topBottomMargin
    );
    const height = availableHeight - topBottomMargin;

    return {
        local: {
            height,
            width: Math.floor(interfaceConfig.LOCAL_THUMBNAIL_RATIO * height)
        },
        remote: {
            height,
            width: Math.floor(interfaceConfig.REMOTE_THUMBNAIL_RATIO * height)
        }
    };
}

/**
 * Calculates the size for thumbnails when in tile view layout.
 *
 * @param {Object} dimensions - The desired dimensions of the tile view grid.
 * @returns {{height, width}}
 */
export function calculateThumbnailSizeForTileView({
    columns,
    visibleRows,
    clientWidth,
    clientHeight,
    disableResponsiveTiles
}: Object) {
    let aspectRatio = TILE_ASPECT_RATIO;

    if (!disableResponsiveTiles && clientWidth < ASPECT_RATIO_BREAKPOINT) {
        aspectRatio = SQUARE_TILE_ASPECT_RATIO;
    }

    const viewWidth = clientWidth - TILE_VIEW_SIDE_MARGINS;
    const viewHeight = clientHeight - TILE_VIEW_SIDE_MARGINS;
    const initialWidth = viewWidth / columns;
    const aspectRatioHeight = initialWidth / aspectRatio;
    const height = Math.floor(
    Math.min(aspectRatioHeight, viewHeight / visibleRows)
    );
    const width = Math.floor(aspectRatio * height);

    return {
        height,
        width
    };
}

/**
 * Returns the width of the visible area (doesn't include the left margin/padding) of the the vertical filmstrip.
 *
 * @returns {number} - The width of the vertical filmstrip.
 */
export function getVerticalFilmstripVisibleAreaWidth() {
    // Adding 11px for the 2px right margin, 2px borders on the left and right and 5px right padding.
    // Also adding 7px for the scrollbar. Note that we are not counting the left margins and paddings because this
    // function is used for calculating the available space and they are invisible.
    // TODO: Check if we can remove the left margins and paddings from the CSS.
    // FIXME: This function is used to calculate the size of the large video, etherpad or shared video. Once everything
    // is reactified this calculation will need to move to the corresponding components.
    const filmstripMaxWidth = (interfaceConfig.FILM_STRIP_MAX_HEIGHT || 120) + 18;

    return Math.min(filmstripMaxWidth, window.innerWidth);
}

/**
 * Computes information that determine the display mode.
 *
 * @param {Object} input - Obejct containing all necessary information for determining the display mode for
 * the thumbnail.
 * @returns {number} - One of <tt>DISPLAY_VIDEO</tt>, <tt>DISPLAY_AVATAR</tt> or <tt>DISPLAY_BLACKNESS_WITH_NAME</tt>.
 */
export function computeDisplayMode(input: Object) {
    const {
        isAudioOnly,
        isCurrentlyOnLargeVideo,
        isScreenSharing,
        canPlayEventReceived,
        isHovered,
        isRemoteParticipant,
        tileViewActive
    } = input;
    const adjustedIsVideoPlayable
    = input.isVideoPlayable && (!isRemoteParticipant || canPlayEventReceived);

    if (!tileViewActive && isScreenSharing && isRemoteParticipant) {
        return isHovered ? DISPLAY_AVATAR_WITH_NAME : DISPLAY_AVATAR;
    } else if (isCurrentlyOnLargeVideo && !tileViewActive) {
    // Display name is always and only displayed when user is on the stage
        return adjustedIsVideoPlayable && !isAudioOnly
            ? DISPLAY_BLACKNESS_WITH_NAME
            : DISPLAY_AVATAR_WITH_NAME;
    } else if (adjustedIsVideoPlayable && !isAudioOnly) {
    // check hovering and change state to video with name
        return isHovered ? DISPLAY_VIDEO_WITH_NAME : DISPLAY_VIDEO;
    }

    // check hovering and change state to avatar with name
    return isHovered ? DISPLAY_AVATAR_WITH_NAME : DISPLAY_AVATAR;
}

/**
 * Calculates the Accruate Height and Width of Thumbnail in Tile View.
 *
 * @returns {object} - Height and Width of the Thumbnail in TileView According to the New UI
 */
export function calculateTileViewThumbnailDimensions(state) {
    // Count the same list the thumbnails are rendered from. getParticipantCountWithFake
    // includes occupants this viewer is not allowed to see -- presenters, and anyone a
    // moderator hid -- so sizing from it reserved a grid slot for a tile that is never
    // drawn, leaving a hole in tile view next to under-sized tiles. Mirrors
    // getTileViewGridDimensions, which already counts the filtered list.
    const audienceLayoutActive = isAudienceLayoutActive(state);
    const isSharedVideoActive = !audienceLayoutActive && Boolean(
        isVideoPlaying(state)
        || getSharedVideoParticipant(state)
        || state['features/shared-video']?.ownerId
    );
    const isWhiteboardActive = !audienceLayoutActive && Boolean(isSharedThumbnailEnabled(state));
    const sharedTileCount = (isSharedVideoActive ? 1 : 0) + (isWhiteboardActive ? 1 : 0);

    const remoteLength = getWebinarAudienceRemoteParticipants(state).length;
    const participantsLength = audienceLayoutActive
        ? remoteLength
        : Math.max(1, remoteLength
            + (state['features/base/config'].iAmRecorder || iAmVisitor(state) ? 0 : 1)
            + sharedTileCount);
    const _isMobile = isMobileResponsive();
    const { conferenceView } = state['features/base/conference'];
    const margins = conferenceView === 'float' ? '90px' : '165px';

    let height;
    let width;

    if (!_isMobile) {
        if (participantsLength === 2 || participantsLength === 3) {
            height = `calc(100vh - ${margins})`;
            width = '100%';
        } else if (participantsLength >= 4 && participantsLength <= 10) {
            height = `calc((100vh - ${margins})/2)`;
            width = '100%';
        } else if (participantsLength > 10 && participantsLength <= 21) {
            height = `calc((100vh - ${margins})/3)`;
            width = '100%';
        } else {
            height = `calc((100vh - ${margins})/4)`;
            width = '100%';
        }
    }

    if (_isMobile) {
        switch (participantsLength) {
        case 2:
            height = `calc((100vh - ${margins})/2)`;
            width = '100%';
            break;
        case 3:
            height = `calc((100vh - ${margins})/2)`;
            width = '100%';
            break;
        case 4:
            height = `calc((100vh - ${margins})/2)`;
            width = '100%';
            break;
        default:
            height = `calc((100vh - ${margins})/3)`;
            width = '100%';
            break;
        }
    }

    return { height,
        width };
}

/**
 * Selects a Participant to have max grid view in Tile View.
 *
 * @returns {object} - Participant to have maximun height or width in TileView According to the New UI
 */
export function selectParticipantforLargeViewGrid(state) {
    const participants = state['features/base/participants'];
    const config = state['features/base/config'];
    const jwt = state['features/base/jwt'];

    // Find key participant types
    const localParticipant = participants.find(p => p.local);
    const isLocalModerator = localParticipant && jwt?.user?.moderator;
    const moderators = participants.filter(p => p.role === 'moderator');
    const pinnedByModerator = participants.find(p => p.moderatorPin === true);
    const { iAmRecorder } = config;

    if (pinnedByModerator) {
        return pinnedByModerator.id;
    }

    if (!iAmRecorder && (pinnedByModerator?.local || isLocalModerator || moderators[0]?.local)) {
        return 'localVideoTileViewContainer';
    }

    if (isLocalModerator && !iAmRecorder) {
        return localParticipant.id;
    }

    if (moderators.length > 0) {
        return moderators[0].id;
    }

    return participants[0]?.id;
}

/**
 * Returns the height of filmstrip in horizontal view mode.
 *
 * @param {Object | Function} [stateful] - The Object or Function that can be
 * resolved to a Redux state object with the toState function.
 * @returns {number} - The height of the filmstrip if visible and not vertical, 0 otherwise.
 */
export function getFilmstripHeight(stateful?: Object | Function) {
    const isVisible = stateful
        ? isFilmstripVisible(stateful)
        : typeof APP !== 'undefined' && APP.store ? isFilmstripVisible(APP.store) : true;

    if (isVisible && !interfaceConfig.VERTICAL_FILMSTRIP) {
        if (typeof $ !== 'undefined' && $('.filmstrip').length) {
            return $('.filmstrip').outerHeight() || 0;
        }
        const filmstrip = document.querySelector('.filmstrip');

        return filmstrip ? (filmstrip: any).offsetHeight : 0;
    }

    return 0;
}

/**
 * Returns the width of the vertical filmstrip if the filmstrip is visible and 0 otherwise.
 *
 * @param {Object | Function} [stateful] - The Object or Function that can be
 * resolved to a Redux state object with the toState function.
 * @returns {number} - The width of the vertical filmstrip if the filmstrip is visible and 0 otherwise.
 */
export function getVerticalFilmstripWidth(stateful?: Object | Function) {
    const isVisible = stateful
        ? isFilmstripVisible(stateful)
        : typeof APP !== 'undefined' && APP.store ? isFilmstripVisible(APP.store) : true;

    return isVisible ? getVerticalFilmstripVisibleAreaWidth() : 0;
}

/**
 * Resizes thumbnails for tile view.
 *
 * @param {number} width - The new width of the thumbnails.
 * @param {number} height - The new height of the thumbnails.
 * @param {boolean} [forceUpdate=false] - Whether to force update hidden local thumb.
 * @returns {void}
 */
export function resizeThumbnailsForTileView(width: number, height: number, forceUpdate: boolean = false) {
    const localThumb = document.getElementById('localVideoContainer');
    const remoteContainer = document.getElementById('filmstripRemoteVideosContainer');

    if (localThumb && (forceUpdate || !localThumb.classList.contains('hidden'))) {
        localThumb.style.setProperty('padding-top', 'unset', 'important');
        localThumb.style.height = `${height}px`;
        localThumb.style.minHeight = `${height}px`;
        localThumb.style.minWidth = `${width}px`;
        localThumb.style.width = `${width}px`;
    }

    if (remoteContainer) {
        const remoteSpans = remoteContainer.querySelectorAll(':scope > span');

        remoteSpans.forEach(span => {
            if (forceUpdate || span.offsetParent !== null) {
                span.style.setProperty('padding-top', 'unset', 'important');
                span.style.height = `${height}px`;
                span.style.minHeight = `${height}px`;
                span.style.minWidth = `${width}px`;
                span.style.width = `${width}px`;
            }
        });
    }
}

/**
 * Resizes thumbnails for horizontal view.
 *
 * @param {Object} dimensions - The new dimensions of the thumbnails.
 * @param {boolean} [forceUpdate=false] - Whether to force update hidden local thumb.
 * @returns {void}
 */
export function resizeThumbnailsForHorizontalView(
        { local = {}, remote = {} }: Object,
        forceUpdate: boolean = false
) {
    const localThumb = document.getElementById('localVideoContainer');
    const remoteContainer = document.getElementById('filmstripRemoteVideosContainer');

    if (localThumb && (forceUpdate || !localThumb.classList.contains('hidden'))) {
        const { height, width } = local;

        if (height !== undefined && width !== undefined) {
            localThumb.style.height = `${height}px`;
            localThumb.style.minHeight = `${height}px`;
            localThumb.style.minWidth = `${width}px`;
            localThumb.style.width = `${width}px`;
        }
    }

    if (remoteContainer) {
        const { height, width } = remote;

        if (height !== undefined && width !== undefined) {
            const remoteSpans = remoteContainer.querySelectorAll(':scope > span');

            remoteSpans.forEach(span => {
                if (forceUpdate || span.offsetParent !== null) {
                    span.style.height = `${height}px`;
                    span.style.minHeight = `${height}px`;
                    span.style.minWidth = `${width}px`;
                    span.style.width = `${width}px`;
                }
            });
        }
    }
}

/**
 * Resizes thumbnails for vertical view.
 *
 * @returns {void}
 */
export function resizeThumbnailsForVerticalView() {
    const localThumb = document.getElementById('localVideoContainer');
    const remoteContainer = document.getElementById('filmstripRemoteVideosContainer');

    if (localThumb && !localThumb.classList.contains('hidden')) {
        const heightToWidthPercent = 100 / interfaceConfig.LOCAL_THUMBNAIL_RATIO;

        localThumb.style.paddingTop = `${heightToWidthPercent}%`;
        localThumb.style.width = '';
        localThumb.style.height = '';
        localThumb.style.minWidth = '';
        localThumb.style.minHeight = '';
    }

    if (remoteContainer) {
        const heightToWidthPercent = 100 / interfaceConfig.REMOTE_THUMBNAIL_RATIO;
        const remoteSpans = remoteContainer.querySelectorAll(':scope > span');

        remoteSpans.forEach(span => {
            if (span.offsetParent !== null) {
                span.style.paddingTop = `${heightToWidthPercent}%`;
                span.style.width = '';
                span.style.height = '';
                span.style.minWidth = '';
                span.style.minHeight = '';
            }
        });
    }
}

let _activeTooltipTimeout: ?number;

/**
 * Shows a tooltip inside the thumbnail container indicating that
 * double-clicking is required to change the view.
 *
 * @param {HTMLElement|null} container - Thumbnail container element.
 * @returns {void}
 */
export function showTileViewDoubleClickTooltip(container: ?HTMLElement): void {
    if (!container) {
        return;
    }

    hideActiveDoubleClickTooltip();

    let text = 'Double click to change the view';

    try {
        if (typeof APP !== 'undefined' && APP.translation && typeof APP.translation.translateString === 'function') {
            const translated = APP.translation.translateString('videothumbnail.doubleClickToChangeView');

            if (translated && translated !== 'videothumbnail.doubleClickToChangeView') {
                text = translated;
            }
        }
    } catch {
        // fallback to default text
    }

    const tooltip = document.createElement('div');

    tooltip.className = 'tile-view-double-click-tooltip';
    tooltip.innerText = text;

    container.appendChild(tooltip);

    // Trigger entrance animation on next frame
    requestAnimationFrame(() => {
        if (tooltip.parentNode) {
            tooltip.classList.add('visible');
        }
    });

    // Auto-hide after 2000ms
    _activeTooltipTimeout = window.setTimeout(() => {
        hideActiveDoubleClickTooltip();
    }, 2000);
}

/**
 * Hides and removes the currently active double-click tooltip across all containers.
 *
 * @returns {void}
 */
export function hideActiveDoubleClickTooltip(): void {
    if (_activeTooltipTimeout) {
        window.clearTimeout(_activeTooltipTimeout);
        _activeTooltipTimeout = undefined;
    }

    if (typeof document === 'undefined') {
        return;
    }

    const tooltips = document.querySelectorAll('.tile-view-double-click-tooltip');

    tooltips.forEach(el => {
        el.classList.remove('visible');
        el.classList.add('fade-out');
        window.setTimeout(() => {
            if (el.parentNode) {
                el.parentNode.removeChild(el);
            }
        }, 200);
    });
}

/**
 * Handles click events on participant thumbnails, managing double-click requirement
 * in tile view, tooltip guidance, and PiP pin/unpin logic.
 *
 * @param {Object} options - Click options.
 * @returns {boolean}
 */
export function handleThumbnailClick({
    event,
    id,
    dispatch,
    filmStripView,
    pinnedParticipant,
    isPiP,
    firstClickTimer,
    setFirstClickTimer,
    container
}: {
    container?: ?HTMLElement,
    dispatch?: Function,
    event?: any,
    filmStripView?: string,
    firstClickTimer?: ?number,
    id?: string,
    isPiP?: boolean,
    pinnedParticipant?: Object,
    setFirstClickTimer: Function
}): boolean {
    const target = event && (event.target || event.srcElement);

    if (target?.closest) {
        if (target.closest('[data-ignore-pin]')
            || target.closest('.displayNameContainer')
            || target.closest('.popover')
            || target.closest('.contexify')) {
            return false;
        }
    }

    const isMobile = isMobileBrowser();
    const isTileView = filmStripView === LAYOUTS.TILE_VIEW;

    // On desktop, when in tile view and not in PiP, require a double-click to switch views.
    if (!isMobile && !isPiP && isTileView) {
        if (!firstClickTimer) {
            const timer = window.setTimeout(() => {
                setFirstClickTimer(undefined);
            }, 300);

            setFirstClickTimer(timer);
            showTileViewDoubleClickTooltip(container);

            return false;
        }

        window.clearTimeout(firstClickTimer);
        setFirstClickTimer(undefined);
        hideActiveDoubleClickTooltip();
    }

    if (event?.stopPropagation) {
        event.stopPropagation();
        if (event.preventDefault) {
            event.preventDefault();
        }
    }

    if (!id || !dispatch) {
        return false;
    }

    const isSameParticipantPinned = pinnedParticipant?.id === id;

    // In desktop non-PiP, exit tile view when pinning. In PiP, allow user to stay in tile view.
    if (!isPiP) {
        exitTileViewIfNeeded(dispatch, filmStripView);
    }

    // If in TILE VIEW and clicking on already pinned participant → do nothing (or unpin in PiP)
    if (isTileView && isSameParticipantPinned) {
        if (isPiP) {
            dispatch(pinParticipant(null, false));
            dispatch(setCustomParticipantVisible(false));
        }

        return false;
    }

    // If in TILE VIEW and clicking on another participant → pin new participant
    if (isTileView && !isSameParticipantPinned) {
        dispatch(pinParticipant(id, true));
        dispatch(setCustomParticipantVisible(false));

        return false;
    }

    const participantIdToPin = isSameParticipantPinned ? null : id;

    dispatch(pinParticipant(participantIdToPin));
    dispatch(setCustomParticipantVisible(false));

    return false;
}

/**
 * Updates the CSS transform on the thumbnail video element based on the synchronized zoom state.
 *
 * @param {Object} options - Zoom update options.
 * @param {HTMLElement} [options.container] - The thumbnail container.
 * @param {boolean} [options.isLocal] - Whether the thumbnail is the local participant's.
 * @param {boolean} [options.localFlipX] - Whether the local camera is mirrored.
 * @param {string} [options.objectFit] - Object fit used when the video element sets none.
 * @param {Object} [options.zoomState] - The zoom from the zoom-sync feature.
 * @returns {void}
 */
export function updateThumbnailZoomTransform({
    container,
    isLocal,
    localFlipX,
    objectFit = 'cover',
    zoomState
}: {
    container?: ?HTMLElement,
    isLocal?: boolean,
    localFlipX?: boolean,
    objectFit?: string,
    zoomState?: Object
}): void {
    if (!container) {
        return;
    }

    const videoEl = container.querySelector('video');

    if (!videoEl) {
        return;
    }

    const isFlipped = Boolean(isLocal && (videoEl.classList.contains('flipVideoX') || localFlipX));
    const defaultTransform = isFlipped ? 'scale(-1, 1)' : '';
    const boxWidth = container.offsetWidth || videoEl.offsetWidth || 0;
    const boxHeight = container.offsetHeight || videoEl.offsetHeight || 0;
    const zoomed = zoomState && zoomState.zoom > 1 + ZOOM_SYNC_EPSILON;

    if (zoomed && (boxWidth <= 0 || boxHeight <= 0)) {
        return;
    }

    const displayed = zoomed && (isFlipped ? flipZoomStateX(zoomState) : zoomState);
    const transform = displayed && toTransform(
        displayed,
        {
            height: boxHeight,
            width: boxWidth
        },
        videoEl.style.objectFit || objectFit);

    if (transform && transform.zoom > 1 + ZOOM_SYNC_EPSILON) {
        const flipStr = isFlipped ? ' scaleX(-1)' : '';

        // Camera thumbnails keep zoom centered so the video tag doesn't move aside.
        const newTransform = `translate3d(0px, 0px, 0) scale(${transform.zoom})${flipStr}`;

        if (videoEl.style.transform !== newTransform) {
            videoEl.style.transform = newTransform;
        }
        if (videoEl.style.transformOrigin !== '50% 50%') {
            videoEl.style.transformOrigin = '50% 50%';
        }
        if (videoEl.style.transition !== 'transform 0.2s ease-out') {
            videoEl.style.transition = 'transform 0.2s ease-out';
        }
    } else {
        if (videoEl.style.transform !== defaultTransform) {
            videoEl.style.transform = defaultTransform;
        }
        if (videoEl.style.transition) {
            videoEl.style.transition = '';
        }
    }
}

/**
 * IDs of the remote tiles on screen, in slot order. A tile keeps its slot
 * while it stays visible; only someone off screen is placed by priority.
 *
 * @type {Array<string>}
 */
let _visibleIds = [];

/**
 * Whether the participant is a screen share tile.
 *
 * @param {Object} p - The participant.
 * @returns {boolean}
 */
function _isScreenShare(p) {
    return [ p.isFakeParticipant, p.fakeParticipant ].some(type =>
        type === FakeParticipant.LocalScreenShare
        || type === FakeParticipant.RemoteScreenShare
        || type === 'LocalScreenShare'
        || type === 'RemoteScreenShare');
}

/**
 * Returns the thumbnail priority of a participant, lower first: 1 moderator
 * pin, 2 screen share, 3 raised hand, 4 dominant speaker, 5 pin, 6 moderator,
 * 7 camera on, 8 microphone on, 9 everyone else.
 *
 * @param {Object} p - The participant.
 * @param {Set<string>} withVideo - IDs with an unmuted video track.
 * @param {Set<string>} withAudio - IDs with an unmuted audio track.
 * @returns {number}
 */
function _getThumbnailPriority(p, withVideo, withAudio) {
    return [
        p.moderatorPin,
        _isScreenShare(p),
        p.raisedHand,
        p.dominantSpeaker,
        p.pinned,
        p.role === 'moderator',
        withVideo.has(p.id),
        withAudio.has(p.id),
        true
    ].findIndex(Boolean) + 1;
}

/**
 * Splits the remote participants into the tiles to show and the ones left
 * off screen. Tiles already on screen keep their slots, so nobody jumps
 * around; an off-screen participant only takes the slot of the visible tile
 * with the lowest priority, and only when its own priority is higher.
 *
 * @param {Array<Object>} tracks - The tracks from the redux state.
 * @param {Array<Object>} participants - The remote participants.
 * @param {number} limit - How many tiles fit, Infinity for no limit.
 * @returns {{ hidden: Array<Object>, visible: Array<Object> }}
 */
export function sortParticipantsForThumbnails(tracks, participants, limit) {
    const withVideo = new Set();
    const withAudio = new Set();

    for (const t of Array.isArray(tracks) ? tracks : []) {
        if (!t.mhTrack || t.muted) {
            continue;
        }

        // Desktop tracks also match by source name, like
        // getTrackByMediaTypeAndParticipant does.
        const ids = t.videoType === VIDEO_TYPE.DESKTOP
            ? [ t.participantId, t.mhTrack.getSourceName?.() ]
            : [ t.participantId ];
        const target = t.mediaType === MEDIA_TYPE.VIDEO ? withVideo
            : t.mediaType === MEDIA_TYPE.AUDIO ? withAudio : undefined;

        target && ids.forEach(id => target.add(id));
    }

    const ranked = (participants || [])
        .map(p => {
            return {
                p,
                priority: _getThumbnailPriority(p, withVideo, withAudio)
            };
        })
        .sort((a, b) => a.priority - b.priority
            || (a.p.raisedHand && b.p.raisedHand
                ? (a.p.raisedHandTimestamp || 0) - (b.p.raisedHandTimestamp || 0) : 0)
            || (a.p.id || '').localeCompare(b.p.id || ''));
    const byId = new Map(ranked.map(r => [ r.p.id, r ]));

    // The lowest priority visible tile, the latest slot on a tie.
    const worstSlot = slots => {
        let worst = 0;

        slots.forEach((r, i) => {
            if (r.priority >= slots[worst].priority) {
                worst = i;
            }
        });

        return worst;
    };

    const slots = _visibleIds.map(id => byId.get(id)).filter(Boolean);

    while (slots.length > limit) {
        slots.splice(worstSlot(slots), 1);
    }

    const inSlots = new Set(slots.map(r => r.p.id));

    // Best first, so once one candidate cannot take a slot none after it can.
    for (const r of ranked) {
        if (inSlots.has(r.p.id)) {
            continue;
        }

        if (slots.length < limit) {
            slots.push(r);
            continue;
        }

        const worst = worstSlot(slots);

        if (!slots.length || r.priority >= slots[worst].priority) {
            break;
        }

        slots[worst] = r;
    }

    const visible = slots.map(r => r.p);
    const visibleIds = new Set(visible.map(p => p.id));

    _visibleIds = visible.map(p => p.id);

    return {
        hidden: ranked.map(r => r.p).filter(p => !visibleIds.has(p.id)),
        visible
    };
}

/**
 * Selects the remote thumbnails this viewer may see, in display order. It is
 * the one source of thumbnail order: the filmstrip renders it and VideoLayout
 * only toggles visibility on top of it.
 *
 * @param {Object} state - The redux state.
 * @returns {{
 *     audienceRemoteParticipants: Array<Object>,
 *     hiddenParticipants: Array<Object>,
 *     remoteParticipants: Array<Object>,
 *     visibleParticipants: Array<Object>
 * }}
 */
export function getThumbnailParticipants(state) {
    // Cached per state: the sort remembers the visible slots, so it must run
    // once per state however many callers ask.
    if (getThumbnailParticipants._cachedState === state) {
        return getThumbnailParticipants._cachedResult;
    }

    // Under Focus Mode this is only the hosts (plus yourself and anything they
    // present); everywhere else it is the untouched participant list.
    const participants = getFocusModeVisibleParticipants(state);

    // Focus Mode and the webinar audience layout each decide who this viewer
    // may see, and a tile has to pass both.
    const remoteParticipants = (participants || []).filter(p =>
        (
            !p.local
            || p.isFakeParticipant === FakeParticipant.LocalScreenShare
            || p.isFakeParticipant === 'LocalScreenShare'
            || p.fakeParticipant === FakeParticipant.LocalScreenShare
            || p.fakeParticipant === 'LocalScreenShare'
        )
        && p.fakeParticipant !== FakeParticipant.SharedVideo
        && p.fakeParticipant !== FakeParticipant.Whiteboard
        && p.fakeParticipant !== 'SharedVideo'
        && p.fakeParticipant !== 'Whiteboard'
        && p.name !== 'YouTube'
        && p.name !== 'Whiteboard'
        && (
            !p.isFakeParticipant
            || p.isFakeParticipant === FakeParticipant.RemoteScreenShare
            || p.isFakeParticipant === FakeParticipant.LocalScreenShare
            || p.isFakeParticipant === 'RemoteScreenShare'
            || p.isFakeParticipant === 'LocalScreenShare'
        )
    ).filter(p => !isPresenterHiddenForLocal(state, p));
    const focusModeVisibleIds = new Set(participants.map(p => p.id));
    const audienceRemoteParticipants = getWebinarAudienceRemoteParticipants(state)
        .filter(p => focusModeVisibleIds.has(p.id));
    const audienceLayoutActive = isAudienceLayoutActive(state);

    // Remote tile slots: the limit minus the shared thumbnail and the local
    // tile (visitors and the audience layout draw no local tile). Stage view
    // has no limit (VideoLayout adds no "+N" there).
    const tileLimit = state['features/filmstrip']?.tileViewTileLimit;
    const limit = typeof tileLimit === 'number' && tileLimit > 0
        && state['features/base/conference']?.filmStripView !== LAYOUTS.STAGE_VIEW
        ? Math.max(0, tileLimit
            - (!audienceLayoutActive && isSharedThumbnailEnabled(state) ? 1 : 0)
            - (audienceLayoutActive || iAmVisitor(state) ? 0 : 1))
        : Infinity;
    const { hidden, visible } = sortParticipantsForThumbnails(
        state['features/base/tracks'],
        audienceLayoutActive ? audienceRemoteParticipants : remoteParticipants,
        limit);
    const prev = getThumbnailParticipants._cachedResult;

    getThumbnailParticipants._cachedState = state;
    getThumbnailParticipants._cachedResult = {
        audienceRemoteParticipants,
        hiddenParticipants: hidden,
        remoteParticipants,

        // Keep the previous reference when nothing moved, so connected
        // components' shallow compare doesn't re-render the whole filmstrip.
        visibleParticipants: prev && _shallowEqualArrays(prev.visibleParticipants, visible)
            ? prev.visibleParticipants
            : visible
    };

    return getThumbnailParticipants._cachedResult;
}

/**
 * Whether two arrays hold the same items in the same order.
 *
 * @param {Array} a - First array.
 * @param {Array} b - Second array.
 * @private
 * @returns {boolean}
 */
function _shallowEqualArrays(a, b) {
    return a.length === b.length && a.every((item, i) => item === b[i]);
}

